/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { useHistory } from "react-router-dom";

import { makeStyles, alpha } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import CampaignIcon from "@material-ui/icons/RecordVoiceOver";
import AddIcon from "@material-ui/icons/Add";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import { SocketContext } from "../../context/Socket/SocketContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((u) => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }

    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;

    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
      : "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
    minHeight: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    background: theme.palette.type === "dark"
      ? alpha(theme.palette.background.paper, 0.6)
      : alpha("#fff", 0.8),
    backdropFilter: "blur(10px)",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.08)}`,
    borderRadius: "16px 16px 0 0",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      padding: theme.spacing(2),
    },
  },
  titleWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  titleIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 6px 20px rgba(255, 152, 0, 0.35)",
    "& svg": {
      color: "#fff",
      fontSize: 24,
    },
  },
  titleText: {
    fontWeight: 700,
    fontSize: "1.5rem",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #fff 0%, #ccc 100%)"
      : "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  actionsWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  searchField: {
    minWidth: 280,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: theme.palette.type === "dark"
        ? alpha(theme.palette.background.default, 0.5)
        : "#fff",
      transition: "all 0.2s ease",
      "&:hover": {
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
      },
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },
  },
  addButton: {
    borderRadius: 12,
    padding: theme.spacing(1.2, 3),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.95rem",
    background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
    boxShadow: "0 4px 14px rgba(255, 152, 0, 0.35)",
    "&:hover": {
      background: "linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)",
      boxShadow: "0 6px 20px rgba(255, 152, 0, 0.45)",
      transform: "translateY(-1px)",
    },
  },
  mainPaper: {
    flex: 1,
    margin: theme.spacing(0, 2, 2, 2),
    borderRadius: 16,
    overflow: "hidden",
    background: theme.palette.type === "dark"
      ? alpha(theme.palette.background.paper, 0.8)
      : theme.palette.background.paper,
    backdropFilter: "blur(10px)",
    border: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.05)}`,
    boxShadow: theme.palette.type === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.3)"
      : "0 4px 20px rgba(0, 0, 0, 0.08)",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  table: {
    minWidth: 800,
  },
  tableHead: {
    background: theme.palette.type === "dark"
      ? alpha(theme.palette.background.default, 0.5)
      : alpha(theme.palette.grey[100], 0.8),
    "& th": {
      fontWeight: 700,
      color: theme.palette.text.primary,
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      padding: theme.spacing(2),
      whiteSpace: "nowrap",
    },
  },
  tableRow: {
    transition: "all 0.2s ease",
    "&:hover": {
      background: theme.palette.type === "dark"
        ? alpha(theme.palette.primary.main, 0.08)
        : alpha(theme.palette.primary.main, 0.04),
    },
    "& td": {
      padding: theme.spacing(1.5, 2),
      borderBottom: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.06) : alpha("#000", 0.06)}`,
    },
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "0.75rem",
    borderRadius: 8,
    height: 28,
  },
  statusInactive: {
    background: alpha(theme.palette.grey[500], 0.15),
    color: theme.palette.grey[600],
  },
  statusProgrammed: {
    background: alpha(theme.palette.info.main, 0.15),
    color: theme.palette.info.main,
  },
  statusInProgress: {
    background: alpha(theme.palette.warning.main, 0.15),
    color: theme.palette.warning.dark,
  },
  statusCanceled: {
    background: alpha(theme.palette.error.main, 0.15),
    color: theme.palette.error.main,
  },
  statusFinished: {
    background: alpha(theme.palette.success.main, 0.15),
    color: theme.palette.success.main,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    transition: "all 0.2s ease",
    color: theme.palette.text.secondary,
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  editButton: {
    "&:hover": {
      background: alpha(theme.palette.primary.main, 0.15),
      color: theme.palette.primary.main,
    },
  },
  deleteButton: {
    "&:hover": {
      background: alpha(theme.palette.error.main, 0.15),
      color: theme.palette.error.main,
    },
  },
  reportButton: {
    "&:hover": {
      background: alpha(theme.palette.info.main, 0.15),
      color: theme.palette.info.main,
    },
  },
  pauseButton: {
    "&:hover": {
      background: alpha(theme.palette.warning.main, 0.15),
      color: theme.palette.warning.main,
    },
  },
  playButton: {
    "&:hover": {
      background: alpha(theme.palette.success.main, 0.15),
      color: theme.palette.success.main,
    },
  },
  cellText: {
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  cellTextSecondary: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
}));

const Campaigns = () => {
  const classes = useStyles();

  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);

  const { datetimeToClient } = useDate();

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-campaign`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [socketManager]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return i18n.t("campaigns.status.inactive");
      case "PROGRAMADA":
        return i18n.t("campaigns.status.programmed");
      case "EM_ANDAMENTO":
        return i18n.t("campaigns.status.inProgress");
      case "CANCELADA":
        return i18n.t("campaigns.status.canceled");
      case "FINALIZADA":
        return i18n.t("campaigns.status.finished");
      default:
        return val;
    }
  };

  const getStatusClass = (val) => {
    switch (val) {
      case "INATIVA":
        return classes.statusInactive;
      case "PROGRAMADA":
        return classes.statusProgrammed;
      case "EM_ANDAMENTO":
        return classes.statusInProgress;
      case "CANCELADA":
        return classes.statusCanceled;
      case "FINALIZADA":
        return classes.statusFinished;
      default:
        return classes.statusInactive;
    }
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <MainContainer className={classes.mainContainer}>
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${
            deletingCampaign.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <CampaignModal
        resetPagination={() => {
          setPageNumber(1);
          fetchCampaigns();
        }}
        open={campaignModalOpen}
        onClose={handleCloseCampaignModal}
        aria-labelledby="form-dialog-title"
        campaignId={selectedCampaign && selectedCampaign.id}
      />
      
      {/* Modern Header */}
      <Box className={classes.header}>
        <Box className={classes.titleWrapper}>
          <Box className={classes.titleIcon}>
            <CampaignIcon />
          </Box>
          <Typography className={classes.titleText}>
            {i18n.t("campaigns.title")}
          </Typography>
        </Box>
        <Box className={classes.actionsWrapper}>
          <TextField
            className={classes.searchField}
            placeholder={i18n.t("campaigns.searchPlaceholder")}
            type="search"
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            className={classes.addButton}
            onClick={handleOpenCampaignModal}
            startIcon={<AddIcon />}
          >
            {i18n.t("campaigns.buttons.add")}
          </Button>
        </Box>
      </Box>

      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        <Table size="small" className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell align="left">
                {i18n.t("campaigns.table.name")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("campaigns.table.status")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("campaigns.table.contactList")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("campaigns.table.whatsapp")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("campaigns.table.scheduledAt")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("campaigns.table.completedAt")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("campaigns.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {campaigns.map((campaign) => (
                <TableRow key={campaign.id} className={classes.tableRow}>
                  <TableCell align="left">
                    <Typography className={classes.cellText}>
                      {campaign.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={formatStatus(campaign.status)}
                      size="small"
                      className={`${classes.statusChip} ${getStatusClass(campaign.status)}`}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography className={classes.cellText}>
                      {campaign.contactListId
                        ? campaign.contactList.name
                        : <span className={classes.cellTextSecondary}>{i18n.t("campaigns.table.notDefined")}</span>}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography className={classes.cellText}>
                      {campaign.whatsappId
                        ? campaign.whatsapp.name
                        : <span className={classes.cellTextSecondary}>{i18n.t("campaigns.table.notDefined2")}</span>}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography className={classes.cellTextSecondary}>
                      {campaign.scheduledAt
                        ? datetimeToClient(campaign.scheduledAt)
                        : i18n.t("campaigns.table.notScheduled")}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography className={classes.cellTextSecondary}>
                      {campaign.completedAt
                        ? datetimeToClient(campaign.completedAt)
                        : i18n.t("campaigns.table.notConcluded")}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center">
                      {campaign.status === "EM_ANDAMENTO" && (
                        <Tooltip title={i18n.t("campaigns.table.stopCampaign")}>
                          <IconButton
                            onClick={() => cancelCampaign(campaign)}
                            size="small"
                            className={`${classes.actionButton} ${classes.pauseButton}`}
                          >
                            <PauseCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {campaign.status === "CANCELADA" && (
                        <Tooltip title="Reiniciar campanha">
                          <IconButton
                            onClick={() => restartCampaign(campaign)}
                            size="small"
                            className={`${classes.actionButton} ${classes.playButton}`}
                          >
                            <PlayCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Ver relatório">
                        <IconButton
                          onClick={() =>
                            history.push(`/campaign/${campaign.id}/report`)
                          }
                          size="small"
                          className={`${classes.actionButton} ${classes.reportButton}`}
                        >
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleEditCampaign(campaign)}
                          className={`${classes.actionButton} ${classes.editButton}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setConfirmModalOpen(true);
                            setDeletingCampaign(campaign);
                          }}
                          className={`${classes.actionButton} ${classes.deleteButton}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={7} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Campaigns;
